/**
 * "Evo" on the event gap strips (2026-10-20 owner ask, after the Dex's Evo
 * toggle: "add that to profile info blocks so that if selected it adds the
 * evolutions on events and quick add, so that someone can easily update the
 * entire line").
 *
 * One per-device switch, read by every event gap strip (the Profile's Quick
 * update card and Catch radar rows, the Calendar event cards): on, each
 * species an event features brings its whole evolution line to the strip
 * (lib/eventEvoLines), so a Community Day's three stages are three tiles and
 * one commit. Off, the strips show exactly what the event lists, as before.
 *
 * Per device, like the Profile block folds: a trainer who ticks lines wants
 * to tick lines on every strip, and the choice is not worth a server round
 * trip. Guarded storage reads: a private window just starts off.
 */
import { useSyncExternalStore } from "react";

export const EVO_LINES_EVENT = "nexus:evo-lines-changed";
const KEY = "nexus.profile.evoLines.v1";

let cache: boolean | null = null;

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function readEvoLines(): boolean {
  if (cache != null) return cache;
  try {
    cache = storage()?.getItem(KEY) === "1";
  } catch {
    cache = false;
  }
  return cache;
}

export function writeEvoLines(on: boolean): void {
  cache = on;
  try {
    if (on) storage()?.setItem(KEY, "1");
    else storage()?.removeItem(KEY);
  } catch {
    /* storage blocked: the cache holds the choice for this session */
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVO_LINES_EVENT));
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(EVO_LINES_EVENT, onChange);
  return () => window.removeEventListener(EVO_LINES_EVENT, onChange);
}

/** The switch as React state: every strip re-renders when any toggle flips it. */
export function useEvoLines(): [boolean, (on: boolean) => void] {
  const on = useSyncExternalStore(subscribe, readEvoLines, () => false);
  return [on, writeEvoLines];
}

export function __resetEvoLinesForTest(): void {
  cache = null;
  try {
    storage()?.removeItem(KEY);
  } catch {
    /* nothing to reset */
  }
}
